/**
 * Persistente localStorage-state voor live-updates (port van NovaDirect).
 *
 * Losgetrokken van de coordinator (liveUpdate.js) zodat de crash-watcher en
 * de coordinator beide bij recordFailContext e.d. kunnen zonder circulaire
 * imports.
 */

const STORAGE_DISMISS = "app.liveUpdate.dismiss";
const STORAGE_ACTIVE = "app.liveUpdate.active"; // { version, activatedAt }
const STORAGE_BLACKLIST = "app.liveUpdate.blacklist"; // { version: blacklistedAt }
const STORAGE_JUST_UPDATED = "app.liveUpdate.justUpdated";
const STORAGE_REVERT_COUNTS = "app.liveUpdate.revertCounts";
// Reden van de laatste rollback, gezet door wie 'm veroorzaakte (crash-watcher)
// vlak vóór de reset, en uitgelezen na de reboot om de telemetrie te verrijken.
const STORAGE_FAIL_CONTEXT = "app.liveUpdate.failContext";
// Lokale wachtrij van nog-niet-verstuurde mislukking-meldingen. Overleeft de
// post-update WebView-reload en een offline boot; wordt geflusht in check().
const STORAGE_PENDING_REPORTS = "app.liveUpdate.pendingReports";

// Hoe vaak een activatie van dezelfde versie mag terugrollen voor we 'm
// lokaal blacklisten. 1 mislukking kan een toevallige hapering zijn (toestel
// gekilld binnen het notifyAppReady-venster); bij de 2e is de bundle stuk en
// moeten we de download/prompt-lus doorbreken.
export const MAX_FAILED_ACTIVATIONS = 2;

const JUST_UPDATED_TTL_MS = 10 * 60 * 1000;

const MAX_PENDING_REPORTS = 20;

// Hoe lang een versie geblacklist blijft. Lang genoeg dat een echt kapotte
// bundle niet binnen één sessie terugkomt, kort genoeg dat een gefixte
// herpush onder dezelfde versie er uiteindelijk weer doorheen komt.
const BLACKLIST_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function readBlacklistMap() {
    try {
        const raw = window.localStorage.getItem(STORAGE_BLACKLIST);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

function writeBlacklistMap(map) {
    try {
        window.localStorage.setItem(STORAGE_BLACKLIST, JSON.stringify(map));
    } catch {
        /* ignore */
    }
}

/**
 * Actieve (niet-verlopen) blacklist-versies. Verlopen entries worden gesnoeid
 * en weggeschreven, zodat een versie na de TTL weer aangeboden mag worden.
 */
export function readBlacklist() {
    const map = readBlacklistMap();
    const now = Date.now();
    const kept = {};
    const active = [];
    for (const [version, ts] of Object.entries(map)) {
        if (now - ts <= BLACKLIST_TTL_MS) {
            kept[version] = ts;
            active.push(version);
        }
    }
    if (active.length !== Object.keys(map).length) writeBlacklistMap(kept);
    return active;
}

/**
 * Voeg een versie toe aan de lokale blacklist (idempotent; ververst de TTL bij
 * een nieuwe mislukking). Gebruikt door zowel de crash-watcher als de
 * boot-detectie van een door capgo teruggerolde update.
 */
export function addToBlacklist(version) {
    if (!version || version === "builtin") return;
    const map = readBlacklistMap();
    map[version] = Date.now();
    writeBlacklistMap(map);
}

/**
 * Tijdstip (epoch ms) waarop `version` lokaal is geblacklist, of null als 'ie
 * niet (meer) actief geblacklist is (afwezig of TTL verlopen).
 */
export function blacklistedSince(version) {
    const map = readBlacklistMap();
    const ts = map[version];
    if (typeof ts !== "number") return null;
    if (Date.now() - ts > BLACKLIST_TTL_MS) return null;
    return ts;
}

/** Verwijder één versie uit de blacklist (idempotent; geen write als afwezig). */
export function clearBlacklistEntry(version) {
    const map = readBlacklistMap();
    if (!(version in map)) return;
    delete map[version];
    writeBlacklistMap(map);
}

/**
 * Mag check() deze versie overslaan vanwege de lokale blacklist?
 *
 * Ja, tenzij de release ná het blacklisten (opnieuw) is gepromoot: dat is het
 * expliciete "ik heb 'm geverifieerd, probeer opnieuw"-signaal van de admin en
 * doorbreekt een permanente blacklist zonder dat de versie-string verandert.
 */
export function blacklistBlocks(version, promotedAt) {
    const since = blacklistedSince(version);
    if (since === null) return false;
    if (typeof promotedAt === "number" && promotedAt > since) return false;
    return true;
}

/** Stempel "bundle X is zojuist geactiveerd" t.b.v. de crash-watcher. */
export function markBundleActivated(version) {
    try {
        window.localStorage.setItem(STORAGE_ACTIVE, JSON.stringify({ version, activatedAt: Date.now() }));
    } catch {
        /* ignore */
    }
}

export function readActiveBundle() {
    try {
        const raw = window.localStorage.getItem(STORAGE_ACTIVE);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

/**
 * Stempel de reden van een op handen zijnde rollback, zodat consumeUpdateOutcome
 * na de reboot kan onderscheiden of het een crash-rollback was (ons) of een
 * capgo-revert (niemand zette een reden). Aangeroepen door de crash-watcher.
 */
export function recordFailContext(version, reason, detail) {
    if (typeof window === "undefined" || !version || version === "builtin") return;
    try {
        window.localStorage.setItem(STORAGE_FAIL_CONTEXT, JSON.stringify({ version, reason, detail }));
    } catch {
        /* ignore */
    }
}

function consumeFailContext(version) {
    try {
        const raw = window.localStorage.getItem(STORAGE_FAIL_CONTEXT);
        if (!raw) return null;
        window.localStorage.removeItem(STORAGE_FAIL_CONTEXT);
        const parsed = JSON.parse(raw);
        return parsed.version === version ? parsed : null;
    } catch {
        return null;
    }
}

/** Zet een mislukking-melding in de lokale wachtrij (best-effort, begrensd). */
export function enqueueFailureReport(report) {
    try {
        const raw = window.localStorage.getItem(STORAGE_PENDING_REPORTS);
        const list = raw ? JSON.parse(raw) : [];
        list.push(report);
        // Bewaar alleen de meest recente N — telemetrie mag nooit onbeperkt groeien.
        window.localStorage.setItem(STORAGE_PENDING_REPORTS, JSON.stringify(list.slice(-MAX_PENDING_REPORTS)));
    } catch {
        /* ignore */
    }
}

export function readPendingReports() {
    try {
        const raw = window.localStorage.getItem(STORAGE_PENDING_REPORTS);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

export function clearPendingReports() {
    try {
        window.localStorage.removeItem(STORAGE_PENDING_REPORTS);
    } catch {
        /* ignore */
    }
}

/**
 * Stempel "we gaan nu naar versie X" vlak voor de WebView-reload. Na de
 * reload leest de bootstrap dit terug om de uitkomst te bepalen.
 */
export function markJustUpdated(version) {
    try {
        window.localStorage.setItem(STORAGE_JUST_UPDATED, JSON.stringify({ version, at: Date.now() }));
    } catch {
        /* ignore */
    }
}

export function readRevertCounts() {
    try {
        const raw = window.localStorage.getItem(STORAGE_REVERT_COUNTS);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function bumpRevertCount(version) {
    const counts = readRevertCounts();
    const next = (counts[version] || 0) + 1;
    counts[version] = next;
    try {
        window.localStorage.setItem(STORAGE_REVERT_COUNTS, JSON.stringify(counts));
    } catch {
        /* ignore */
    }
    return next;
}

/** Wis de revert-teller van één versie (idempotent; geen write als afwezig). */
export function clearRevertCount(version) {
    const counts = readRevertCounts();
    if (!(version in counts)) return;
    delete counts[version];
    try {
        window.localStorage.setItem(STORAGE_REVERT_COUNTS, JSON.stringify(counts));
    } catch {
        /* ignore */
    }
}

/**
 * Lees + wis de "we gingen bijwerken naar X"-vlag en bepaal de uitkomst t.o.v.
 * de versie die nu daadwerkelijk draait:
 *  - success:  X draait nu → toon bevestiging.
 *  - reverted: X draait NIET — capgo rolde 'm terug omdat de bundle niet
 *              (tijdig) notifyAppReady aanriep / niet bootte. Tel de mislukking
 *              en blacklist X na MAX_FAILED_ACTIVATIONS zodat check() niet in
 *              een eindeloze download/prompt-lus blijft hangen.
 *  - none:     geen (recente) poging.
 */
export function consumeUpdateOutcome(currentVersion) {
    if (typeof window === "undefined") return { status: "none", version: null };
    let data = null;
    try {
        const raw = window.localStorage.getItem(STORAGE_JUST_UPDATED);
        if (raw) {
            window.localStorage.removeItem(STORAGE_JUST_UPDATED);
            data = JSON.parse(raw);
        }
    } catch {
        /* ignore */
    }

    if (!data || Date.now() - data.at >= JUST_UPDATED_TTL_MS) {
        return { status: "none", version: null };
    }

    if (data.version === currentVersion) {
        // Geslaagd — schoon een eventuele eerdere revert-teller voor deze versie op.
        clearRevertCount(data.version);
        return { status: "success", version: data.version };
    }

    // Reden ophalen die de crash-watcher eventueel zette vlak vóór z'n reset;
    // anders is dit een capgo-revert (bundle bootte/notify't niet).
    const failCtx = consumeFailContext(data.version);
    const reason = (failCtx && failCtx.reason) || "no_notify_revert";
    const detail = failCtx ? failCtx.detail : undefined;

    const failures = bumpRevertCount(data.version);
    const blacklisted = failures >= MAX_FAILED_ACTIVATIONS;
    if (blacklisted) addToBlacklist(data.version);
    return { status: "reverted", version: data.version, blacklisted, reason, detail };
}

export function readDismiss(version) {
    try {
        const raw = window.localStorage.getItem(STORAGE_DISMISS);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed.version === version) return parsed;
        }
    } catch {
        /* fall through */
    }
    return { version, firstSeenAt: Date.now(), lastDismissedAt: null, dismissCount: 0 };
}

export function writeDismiss(state) {
    try {
        window.localStorage.setItem(STORAGE_DISMISS, JSON.stringify(state));
    } catch {
        /* ignore */
    }
}
